import { Box, Card, Grid, List, ListItem, Switch } from "@mui/material";
import Divider from "@material-ui/core/Divider";
import PropTypes from "prop-types";

import InspectionTimeline from "modules/inspections/components/InspectionTimeline";
import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import { Link, useNavigate } from "react-router-dom";
import SoftTypography from "components/SoftComponents/SoftTypography";
import { wrapper, deviceViewWrapper, loadingScreen } from "utils/wrapper";
import { useEffect, useState } from "react";
import InspectionPreviewCard from "modules/inspections/components/InspectionPreviewCard";

import userAPI from "services/api/user";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import moment from "moment";
import LogoutButton from "components/Buttons/LogoutButton";
import EmailSettings from "pages/components/Settings/EmailSettings";
import SystemAppearance from "pages/components/Settings/SystemAppearance";

function SettingsView({ map, user }) {
    useEffect(() => {
        if (!map.current) return;
        disableMap(map);
    }, []);

    userAPI.loadToken(useAuth0());

    const navigate = useNavigate();
    const [controller] = useSoftUIController();
    const { deviceLayout } = controller;

    const queryClient = useQueryClient();

    const { mutateAsync } = useMutation({
        mutationFn: (values) => {
            return userAPI.processSubscription(values)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getSubscriptionsForUser', user.user.email] });
        }
    })

    const { mutate: toggleActive } = useMutation({
        mutationFn: (body) => {
            return userAPI.updateSubscriptionStatus(body);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getSubscriptionsForUser', user.user.email] });
        },
    });

    const { mutate: updateMode } = useMutation({
        mutationFn: (body) => {
            return userAPI.updateSystemMode({ appearance_type: body });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getUser', user.user.email] });
        },
    });

    /* 
        profile content 
        - system appearance
        - email settings
        - change password?
        - etc.

        content on RHS for desktop???
    */

    const tabs = [
        { value: 0, label: 'Password Manager', /*component: <PasswordManager />*/ },
        { value: 1, label: 'System Visuals', /*component: <SystemAppearance {... {user, deviceLayout, updateMode}} />*/ },
        { value: 2, label: 'Email Configuration', /*component: <EmailSettings {...{ user, deviceLayout, toggleActive}} onSubmit={mutateAsync} />*/ },
        { value: 3, label: 'Translations Blacklist', /*component: <TranslationsBlacklist {...{user, deviceLayout}} />*/ }
    ]

    return wrapper((
        <SoftBox borderRadius="20px" color={"white"} width="100%" height={deviceLayout == 'mobile' ? 'calc(100svh - 4.25rem - 8px)' : "calc(100svh - 5.5rem - 16px)"} minHeight={deviceLayout == 'mobile' && "calc(100svh - 4.25rem - 8px)"}
            sx={({ palette: { white }, functions: { rgba } }) => ({ backgroundColor: rgba(white.main, 0.6), overflowY: 'hidden' })}
        >
            <SoftBox m={1}>
                <Card sx={{ overflow: "visible", width: '100%', borderRadius: '16px' }}>
                    <SoftBox mx={2} mt={1} mb={1}>
                        <SoftBox display='flex' flexDirection='row' justifyContent='space-between' alignItems="center">
                            <SoftBox height="100%" display="flex" flexDirection="column" justifyContent="center">
                                <SoftTypography variant="h5" fontWeight="bold">Account Settings</SoftTypography>
                                {deviceLayout != 'mobile' && (
                                    <SoftTypography variant="body3">Make adjustments to your account</SoftTypography>
                                )}
                            </SoftBox>
                            <SoftBox display="flex" flexDirection="column" justifyContent="center" height="100%">
                                <LogoutButton>
                                    <SoftButton variant='gradient' size={deviceLayout == 'mobile' ? "small" : "medium"} color='dark'>
                                        Log out
                                    </SoftButton>
                                </LogoutButton>
                            </SoftBox>
                        </SoftBox>
                    </SoftBox>
                </Card>
            </SoftBox>
            <SoftBox px={1} display="flex" justifyContent="center" sx={{ height: deviceLayout == 'mobile' ? `calc(100% - 74px)` : `calc(100% - 90px)` }}>
                <Card sx={{ height: '100%', width: '100%' }}>

                    <SoftBox height="100%" display="flex" flexDirection="row">
                        <SoftBox width={'300px'} sx={{ backgroundColor: '#eee', borderRight: '1px solid #bbb' }}>
                            <SoftBox p={3}>
                                <List>
                                    {tabs.map((tab, index) => (
                                        <ListItem>
                                            <SoftBox width='100%' display='flex' flexDirection="column">
                                                <SoftTypography py={2} variant="subtitle2">{tab.label}</SoftTypography>
                                                {(index + 1 < tabs.length) && (
                                                    <Divider />
                                                )}
                                            </SoftBox>
                                        </ListItem>
                                    ))}
                                </List>
                            </SoftBox>
                        </SoftBox>
                        <SoftBox p={{ xxs: 1, xl: 2 }} sx={{ height: '100%', overflowY: 'auto' }}>
                            <SystemAppearance {...{ user, deviceLayout, updateMode }} />
                            <SoftBox my={2}>
                                <Divider />
                            </SoftBox>
                            <EmailSettings {...{ user, deviceLayout, toggleActive }} onSubmit={mutateAsync} />
                        </SoftBox>
                    </SoftBox>
                </Card>
            </SoftBox>
        </SoftBox>
    ), deviceLayout
    );
}

SettingsView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default SettingsView;